import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useKeepSendingRequest } from "go-core/hooks/useKeepSendingRequest";
import { UseKeepSendingRequestState } from "go-core/hooks/utils/types";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { apiOrganization } from "../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, KSeFInvoiceApiStatus } from "../../../../../../../services/Api/Organization/types";

export const useKSeFStatusRequests = (
	fetchInvoice: () => Promise<InvoiceApi | null>,
	invoiceId?: number,
	KSeFInstalled?: boolean
) => {
	const [displayKSeFUninstalledAlert, setKSeFUninstalledAlert] = useState(false);
	const [refetchingInvoiceInProgress, setRefetchingInvoiceInProgress] = useState(false);
	const sendToKSeFStatus = useRef<KSeFInvoiceApiStatus | undefined>(undefined);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { keepSendingRequests } = useKeepSendingRequest(
		(sendingRequestState: UseKeepSendingRequestState) => refetchInvoiceHandler(sendingRequestState),
		{ interval: 3000, customNumberOfRequests: 20 }
	);

	const manageInvoicePreviewRefetching = async () => {
		try {
			sendToKSeFStatus.current = undefined;
			const firstRequestTimestamp = performance.now();
			const KSeFData = await sendInvoiceToKSeFFromInvoicePreviewHandler();
			const { status } = KSeFData ?? {};

			if (status === KSeFInvoiceApiStatus.PENDING) {
				await keepSendingRequests(firstRequestTimestamp);
			} else if (KSeFData) {
				await refetchInvoiceHandler();
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		} finally {
			setRefetchingInvoiceInProgress(false);
		}
	};

	const refetchInvoiceHandler = async (sendingRequestState?: UseKeepSendingRequestState) => {
		const { forceStopSendingRequests } = sendingRequestState ?? {};

		const fetchedInvoice = await fetchInvoice();

		sendToKSeFStatus.current = fetchedInvoice?.ksef_data?.status;
		if (fetchedInvoice?.ksef_data && fetchedInvoice.ksef_data.status !== KSeFInvoiceApiStatus.PENDING) {
			if (forceStopSendingRequests) forceStopSendingRequests.current = true;

			if (fetchedInvoice.ksef_data.status === KSeFInvoiceApiStatus.SUCCESS)
				addSuccessFlash(t("modules.invoice.action.successful_invoice_registration_in_ksef.title"));
		}
	};

	const sendInvoiceToKSeFFromInvoicePreviewHandler = async () => {
		if (!invoiceId || !KSeFInstalled) {
			if (!KSeFInstalled) setKSeFUninstalledAlert(true);

			return null;
		}

		await confirmation({
			title: t("lib:confirmation.title"),
			message: t("lib:confirmation.message.action"),
		});
		setRefetchingInvoiceInProgress(true);

		return apiOrganization.sendInvoiceToKSeF(invoiceId);
	};

	return {
		manageInvoicePreviewRefetching,
		refetchingInvoiceInProgress,
		sendToKSeFStatus,
		displayKSeFUninstalledAlert,
	};
};
